import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { AuditChip } from '@/components/audit/AuditChip';
import { DiscountCodeForm } from '@/features/commerce/DiscountCodeForm';
import {
  useDeleteDiscountCode,
  useDiscountCodes,
} from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import { formatCents, formatPercent } from '@/lib/money';
import { useUiStore } from '@/stores/ui';
import type { DiscountCode } from '@/schemas/admin/commerce/discountCodes';

export const Route = createFileRoute('/_authed/commerce/discount-codes')({
  component: DiscountCodesScreen,
});

type Status = 'active' | 'scheduled' | 'expired';

function statusOf(code: DiscountCode): Status {
  const now = Date.now();
  if (code.starts_at && new Date(code.starts_at).getTime() > now) return 'scheduled';
  if (code.ends_at && new Date(code.ends_at).getTime() < now) return 'expired';
  return 'active';
}

function DiscountCodesScreen() {
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const list = useDiscountCodes();
  const del = useDeleteDiscountCode();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<DiscountCode | null>(null);
  const [deleting, setDeleting] = React.useState<DiscountCode | null>(null);

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(t('discounts.deleted'));
      setDeleting(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  const columns: readonly DataColumn<DiscountCode>[] = [
    {
      id: 'code',
      header: t('discounts.table.code'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-sm font-semibold text-[color:var(--color-fg)]">
          {r.code}
        </span>
      ),
      sortBy: (r) => r.code,
    },
    {
      id: 'kind',
      header: t('discounts.table.kind'),
      cell: (r) => (
        <Badge tone="neutral">
          {r.kind === 'percent' ? t('discounts.form.percent') : t('discounts.form.fixed')}
        </Badge>
      ),
    },
    {
      id: 'value',
      header: t('discounts.table.value'),
      align: 'end',
      cell: (r) => {
        if (r.kind === 'percent') return formatPercent(r.value, locale);
        return formatCents(r.value, locale, r.currency ?? 'USD');
      },
      sortBy: (r) => r.value,
    },
    {
      id: 'usage',
      header: t('discounts.table.usage'),
      align: 'end',
      cell: (r) =>
        typeof r.max_uses === 'number'
          ? `${r.used_count ?? 0} / ${r.max_uses}`
          : `${r.used_count ?? 0}`,
    },
    {
      id: 'partner',
      header: t('discounts.table.partner'),
      cell: (r) => r.partner_tag ?? <span className="text-[color:var(--color-fg-subtle)]">—</span>,
    },
    {
      id: 'window',
      header: t('discounts.table.window'),
      cell: (r) => (
        <div className="flex flex-col text-xs">
          <span>{r.starts_at ? <DateTime value={r.starts_at} /> : '—'}</span>
          <span className="text-[color:var(--color-fg-muted)]">
            {r.ends_at ? <DateTime value={r.ends_at} /> : '—'}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('discounts.table.status'),
      cell: (r) => {
        const s = statusOf(r);
        return (
          <Badge tone={s === 'active' ? 'success' : s === 'scheduled' ? 'info' : 'neutral'}>
            {t(`discounts.status.${s}`)}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: <span className="sr-only">{tc('table.actions')}</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(r)}
            aria-label={t('discounts.edit', { code: r.code })}
          >
            <Pencil aria-hidden className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDeleting(r)}
            aria-label={t('discounts.delete', { code: r.code })}
          >
            <Trash2 aria-hidden className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('discounts.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('discounts.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            {t('discounts.new')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <DataTable<DiscountCode>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: t('discounts.empty.title'), body: t('discounts.empty.body') }}
            initialSort={{ id: 'code', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('discounts.new')}</DialogTitle>
            <DialogDescription>
              {t('discounts.newBody', {
                defaultValue:
                  'Create a discount code. Code is normalized to uppercase and locked once saved.',
              })}
            </DialogDescription>
          </DialogHeader>
          <DiscountCodeForm
            onSaved={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('discounts.editTitle', { code: editing?.code ?? '' })}</DialogTitle>
            <DialogDescription>
              {t('discounts.editBody', {
                defaultValue:
                  'Update value, caps, window, and partner tag. The code itself is immutable.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <DiscountCodeForm
              code={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t('discounts.deleteTitle')}
        description={t('discounts.deleteBody', { code: deleting?.code ?? '' })}
        destructive
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
